import { DeleteTwoTone, EditTwoTone } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Box,
  Button,
  Table as MaterialTable,
  Paper,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import * as React from 'react';
import { useDeleteItem } from 'src/utils/api/hooks';

import { AppStorage } from 'src/utils/storage';
import { PaginatedResults, paginate } from 'src/utils/utils';
import { COLORS } from '../../utils/constants';

export interface HeadCell<T> {
  id: keyof T;
  label: string;
  formatter?: (user: T) => any;
}

interface EnhancedTableProps<T> {
  headCells: HeadCell<T>[];
}

function TableHeader<T>(props: EnhancedTableProps<T>) {
  const { headCells } = props;

  return (
    <TableHead>
      <TableRow>
        {headCells.map(({ id, label }) => {
          return (
            <TableCell key={id as string} align={'center'} padding={'normal'}>
              {label}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

export default function Table<T>({
  headCells,
  data,
  defaultLimit = AppStorage.getItem('defaultLimit', 5),
  minWidth = 750,
  keyField = 'uid' as keyof T
}: {
  headCells: HeadCell<T>[];
  data: T[];
  keyField?: keyof T;
  defaultLimit?: number;
  minWidth?: number;
}) {
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(defaultLimit);

  const [paginaed, setPaginated] = React.useState<PaginatedResults<T>>({
    data: [],
    page: page,
    pageSize: defaultLimit,
    total: 0,
    total_pages: 0
  });
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const limit = parseInt(event.target.value, 10);
    setLimit(limit);
    setPage(1);
    AppStorage.setItem('defaultLimit', limit);
  };
  const emptyRows = paginaed.total <= 0;

  React.useEffect(() => {
    setPaginated(paginate(data, page, limit));
  }, [page, limit, data]);

  React.useEffect(() => {
    setPage(1);
  }, [data.length]);

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <MaterialTable
            sx={{ minWidth }}
            aria-labelledby="tableTitle"
            size={'medium'}
          >
            <TableHeader<T> headCells={headCells} />
            <TableBody>
              {paginaed?.data.map((row, index) => {
                const key = String(row[keyField] ?? index);
                return (
                  <TableRow hover={false} tabIndex={-1} key={key}>
                    {headCells.map(({ id, formatter }) => {
                      const val = formatter ? formatter(row) : row[id];
                      return (
                        <TableCell key={String(id) + key} align={'center'}>
                          {val}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              {emptyRows && (
                <TableRow
                  style={{
                    height: 53
                  }}
                >
                  <TableCell colSpan={6}>
                    {'Pas de données disponible'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </MaterialTable>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          component={'div'}
          labelRowsPerPage={
            <Box sx={{ position: 'absolute', left: 10, top: '25%' }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: 18 }}>
                {paginaed.total || 0}
              </Typography>
            </Box>
          }
          labelDisplayedRows={() => ''}
          count={paginaed.total || 0}
          rowsPerPage={limit}
          page={page - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}

export function TableWithoutPagination<T>({
  data,
  headCells,
  minWidth = 750
}: {
  data: T[];
  headCells: HeadCell<T>[];
  minWidth?: number;
}) {
  const emptyRows = data.length <= 0;

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <MaterialTable
            sx={{ minWidth }}
            aria-labelledby="tableTitle"
            size={'medium'}
          >
            <TableHeader<T> headCells={headCells} />
            <TableBody>
              {data.map((row: any) => {
                return (
                  <TableRow hover tabIndex={-1} key={row._id}>
                    {headCells.map(({ id, formatter }) => {
                      const val = formatter ? formatter(row) : row[id];
                      return (
                        <TableCell key={String(id) + row._id} align={'center'}>
                          {val}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              {emptyRows && (
                <TableRow
                  style={{
                    height: 53
                  }}
                >
                  <TableCell colSpan={6}>
                    {'Pas de données disponible'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </MaterialTable>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export function DeleteButton({
  deleteFunction,
  success,
  error,
  id
}: {
  deleteFunction: any;
  success: string;
  error: string;
  id: string;
}) {
  const onDelete = useDeleteItem(deleteFunction, success, error);
  return (
    <Tooltip title={'Supprimer'} arrow placement="top-start">
      <LoadingButton
        loading={false}
        onClick={() => onDelete(id)}
        color="error"
        variant="text"
        startIcon={<DeleteTwoTone />}
      />
    </Tooltip>
  );
}

export function UpdateButton<T>({
  item,
  modal: Modal,
  ...rest
}: {
  item: T;
  modal: any;
} & any) {
  const [show, setShow] = React.useState(false);
  return (
    <>
      {show && (
        <Modal
          {...rest}
          item={item}
          onHide={() => setShow(false)}
          show={show}
        />
      )}
      <Tooltip title={'Modifier'} arrow placement="top-start">
        <Button
          onClick={() => setShow(true)}
          variant="text"
          startIcon={<EditTwoTone />}
        />
      </Tooltip>
    </>
  );
}

export function SwitchButton({
  updateFunction,
  value
}: {
  value: boolean;
  updateFunction: (value: boolean) => any;
}) {
  const [loading, setLoading] = React.useState(false);
  const onSubmit = async (value: boolean) => {
    setLoading(true);
    try {
      await updateFunction(value);
    } catch (ex) {}
    setLoading(false);
  };

  return (
    <Switch
      color={value ? 'primary' : 'secondary'}
      checked={value}
      disabled={loading}
      onChange={(_, checked) => {
        onSubmit(checked);
      }}
      name="checkedA"
    />
  );
}

export function UserFormatter({
  name,
  avatar,
  matricule
}: {
  name: string;
  avatar: string;
  matricule: string;
}) {
  return (
    <Stack direction={'row'} alignItems={'center'} sx={{ width: 150 }}>
      <Avatar
        sx={{ mr: 2, width: 40, height: 40 }}
        variant="rounded"
        alt={name}
        placeholder={name}
        src={avatar}
      />
      <Box sx={{ textAlign: 'left' }}>
        <Typography variant="body2">{name}</Typography>
        {!!matricule && (
          <Typography
            sx={{ color: COLORS.primary, fontSize: 12 }}
            variant="body2"
          >
            {matricule}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
